import * as React from "react";
import { Spinner, mergeClasses } from "@fluentui/react-components";
import { useEffect, useState } from "react";
import { getIconsByEmail } from "../../services/iconService";
import { getCurrentUserEmail } from "../../services/outlookService";
import { ForboIcon, ForboSignup, Group } from "../../types/models";
import { toSecureIconUrl } from "../../utils/iconUrls";
import { insertIcons } from "../taskpane";
import { useAppStyles } from "./appStyles";
import IconPicker from "./IconPicker";

const App: React.FC = () => {
  const styles = useAppStyles();
  const [groups, setGroups] = useState<Group[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [insertError, setInsertError] = useState<string | null>(null);
  const [signup, setSignup] = useState<ForboSignup | null>(null);

  useEffect(() => {
    let isMounted = true;

    async function loadIcons() {
      setIsLoading(true);
      setLoadError(null);

      try {
        const email = getCurrentUserEmail();
        if (!email) {
          throw new Error("Unable to determine the current mailbox user.");
        }

        const data = await getIconsByEmail(email);
        if (isMounted) {
          const nextGroups = Array.isArray(data.groups) ? data.groups : [];
          setGroups(nextGroups);
          setSignup(data.response === "notfound" ? data.signup ?? null : null);
        }
      } catch (error) {
        if (isMounted) {
          setLoadError(error instanceof Error ? error.message : "Failed to load icons.");
          setGroups([]);
          setSignup(null);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    void loadIcons();

    return () => {
      isMounted = false;
    };
  }, []);

  async function handleInsert(icons: ForboIcon[]): Promise<boolean> {
    setInsertError(null);

    const inserted = await insertIcons(icons);
    if (!inserted) {
      setInsertError("Failed to insert the selected icons into the message.");
    }

    return inserted;
  }

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner label="Loading icons..." />
      </div>
    );
  }

  if (loadError) {
    return <AppMessage message={loadError} tone="error" />;
  }

  if (signup) {
    return <SignupPrompt signup={signup} />;
  }

  if (groups.length === 0) {
    return <AppMessage message="No icons are available for this mailbox." />;
  }

  return (
    <>
      {insertError ? <AppMessage message={insertError} tone="error" compact /> : null}
      <IconPicker groups={groups} onInsert={handleInsert} />
    </>
  );
};

interface SignupPromptProps {
  signup: ForboSignup;
}

const SignupPrompt: React.FC<SignupPromptProps> = ({ signup }) => {
  const styles = useAppStyles();
  const imageWidth = Number(signup.w) || 100;
  const imageHeight = Number(signup.h) || 100;
  const signupLink = toSecureIconUrl(signup.uri);

  return (
    <div className={styles.signupCard}>
      <div className={styles.signupTitle}>{signup.title || "Register"}</div>
      <div className={styles.signupDescription}>
        No icons are currently assigned to this mailbox. Register to request access.
      </div>
      <a className={styles.signupImageLink} href={signupLink} target="_blank" rel="noreferrer">
        <img
          className={styles.signupImage}
          src={toSecureIconUrl(signup.icon)}
          width={imageWidth}
          height={imageHeight}
          alt={signup.title || "Register"}
        />
      </a>
    </div>
  );
};

interface AppMessageProps {
  compact?: boolean;
  message: string;
  tone?: "default" | "error";
}

const AppMessage: React.FC<AppMessageProps> = ({ compact = false, message, tone = "default" }) => {
  const styles = useAppStyles();

  return (
    <div
      className={mergeClasses(
        styles.message,
        compact && styles.messageCompact,
        tone === "error" && styles.messageError
      )}
    >
      {message}
    </div>
  );
};

export default App;
